<template>
  <div class="product-showcase">
    <!-- Hero Section with Background Pattern -->
    <section class="hero-section position-relative overflow-hidden">
      <!-- Coffee Bean Pattern Background -->
      <div class="coffee-pattern-bg position-absolute w-100 h-100"></div>
      
      <!-- Main Content Container -->
      <div class="container-fluid">
        <div class="row align-items-center min-vh-100">
          <!-- Left Side - Product Images -->
          <div class="col-lg-6 col-md-12">
            <div class="product-images-container position-relative">
              <!-- Main Product Image -->
              <div class="main-product-image text-center">
                <ImageWrapper 
                  :src="selectedProduct.image" 
                  :alt="selectedProduct.name"
                  class="img-fluid main-machine-img"
                />
              </div>
              
              <!-- Floating Product Elements -->
              <div class="floating-elements">
                <div class="floating-element floating-element-1">
                  <ImageWrapper src="/static/assets/coffee-capsule-1.png" alt="Coffee Capsule" class="img-fluid" />
                </div>
                <div class="floating-element floating-element-2">
                  <ImageWrapper src="/static/assets/coffee-capsule-2.png" alt="Coffee Capsule" class="img-fluid" />
                </div>
              </div>
            </div>
          </div>
          
          <!-- Right Side - Product Information -->
          <div class="col-lg-6 col-md-12">
            <div class="product-info-container px-4">
              <div class="product-badge mb-3">
                <span class="badge bg-gold text-white px-3 py-2 rounded-pill">
                  {{ $t('common.bestSeller') }}
                </span>
              </div>
              
              <h1 class="product-title mb-4 fw-bold">
                {{ selectedProduct.title }}
              </h1>
              
              <p class="product-description mb-4 text-muted">
                {{ selectedProduct.description }}
              </p>
              
              <div class="product-price mb-4">
                <span class="current-price h3 fw-bold text-dark">
                  {{ selectedProduct.price }}
                </span>
                <span v-if="selectedProduct.originalPrice" class="original-price text-muted txt-delete ms-3">
                  {{ selectedProduct.originalPrice }}
                </span>
              </div>
              
              <!-- Color Selection -->
              <div class="color-selection mb-4">
                <h6 class="mb-3">{{ $t('banner.colorSelected') }}</h6>
                <div class="color-options d-flex flex-wrap gap-2">
                  <button
                    v-for="color in availableColors"
                    :key="color.colorValue"
                    @click="selectColor(color)"
                    :class="['color-option', 'btn', 'rounded-circle', 'p-0', 
                             { 'active': selectedColor.colorValue === color.colorValue }]"
                    :style="{ backgroundColor: color.color }"
                    :title="color.color_name_en"
                  >
                    <span class="sr-only">{{ color.color_name_en }}</span>
                  </button>
                </div>
              </div>
              
              <!-- Action Buttons -->
              <div class="action-buttons d-flex flex-column flex-sm-row gap-3">
                <button class="btn btn-primary btn-lg flex-fill" @click="buyNow">
                  {{ $t('common.buyNow') }}
                </button>
                <button class="btn btn-outline-secondary btn-lg flex-fill" @click="learnMore">
                  {{ $t('common.learnMore') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Product Grid Section -->
    <section class="product-grid-section py-5">
      <div class="container">
        <div class="row">
          <div class="col-12 text-center mb-5">
            <h2 class="section-title fw-bold">{{ $t('products.title') }}</h2>
            <p class="section-subtitle text-muted">{{ $t('products.subtitle') }}</p>
          </div>
        </div>
        
        <div class="row g-4">
          <div 
            v-for="product in productGrid" 
            :key="product.id"
            class="col-lg-3 col-md-4 col-sm-6 col-12"
          >
            <ProductCard 
              :product="product"
              @select="selectProduct"
              class="h-100"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import ProductCard from '../molecules/ProductCard.vue'
import { ImageWrapper } from '~/components/'
import machineData from '../../data/machine.js'

export default {
  name: 'ProductShowcase',
  
  components: {
    ProductCard,
    ImageWrapper
  },
  
  data() {
    return {
      selectedProduct: {
        id: 1,
        name: 'Vertuo Pop Coffee Machine',
        title: 'Vertuo Pop<br/>Coffee Machine',
        description: 'Compact design that fits seamlessly into any kitchen or workspace, adding vibrant colors to your life',
        price: 'HKD 1,088',
        originalPrice: 'HKD 1,288',
        image: '/static/assets/vertuo-pop-main.png'
      },
      selectedColor: machineData.machine[0],
      availableColors: machineData.machine,
      productGrid: [
        {
          id: 1,
          name: 'Vertuo Pop',
          price: 'HKD 1,088',
          image: '/static/assets/product-1.png',
          badge: 'Best Seller'
        },
        {
          id: 2,
          name: 'Citiz & Milk',
          price: 'HKD 2,088',
          image: '/static/assets/product-2.png'
        },
        {
          id: 3,
          name: 'Creatista Plus',
          price: 'HKD 3,588',
          image: '/static/assets/product-3.png'
        },
        {
          id: 4,
          name: 'Essenza Mini',
          price: 'HKD 888',
          image: '/static/assets/product-4.png'
        }
      ]
    }
  },
  
  methods: {
    /**
     * Handles color selection for the product
     * @param {Object} color - Selected color object
     */
    selectColor(color) {
      this.selectedColor = color
      // Update product image based on color selection
      this.updateProductImage()
    },
    
    /**
     * Updates the main product image based on selected color
     */
    updateProductImage() {
      // Logic to update product image based on color
      const colorImageMap = {
        yellow: '/static/assets/vertuo-pop-yellow.png',
        blue: '/static/assets/vertuo-pop-blue.png',
        white: '/static/assets/vertuo-pop-white.png',
        black: '/static/assets/vertuo-pop-black.png',
        aqua: '/static/assets/vertuo-pop-aqua.png',
        red: '/static/assets/vertuo-pop-red.png'
      }
      
      this.selectedProduct.image = colorImageMap[this.selectedColor.colorValue] || this.selectedProduct.image
    },
    
    /**
     * Handles buy now button click
     */
    buyNow() {
      // Emit event or handle purchase logic
      this.$emit('buy-now', {
        product: this.selectedProduct,
        color: this.selectedColor
      })
    },
    
    /**
     * Handles learn more button click
     */
    learnMore() {
      // Navigate to product details or show more info
      this.$emit('learn-more', this.selectedProduct)
    },
    
    /**
     * Handles product selection from grid
     * @param {Object} product - Selected product object
     */
    selectProduct(product) {
      this.selectedProduct = { ...this.selectedProduct, ...product }
      this.$emit('product-selected', product)
    }
  },
  
  mounted() {
    // Initialize with first color
    this.updateProductImage()
  }
}
</script>

<style lang="scss" scoped>
@import "~/scss/include/_commonInclude.scss";

.content .app {
  .product-showcase {
    
    .hero-section {
      background: linear-gradient(135deg, color(whiteAbs) 0%, #f8f9fa 100%);
      min-height: 100vh;
      
      .coffee-pattern-bg {
        background-image: url('/static/assets/coffee-pattern.svg');
        background-repeat: repeat;
        background-size: 100px 100px;
        opacity: 0.05;
        z-index: 1;
      }
      
      .container-fluid {
        position: relative;
        z-index: 2;
      }
    }
    
    .product-images-container {
      .main-machine-img {
        max-width: 400px;
        height: auto;
        filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.1));
        transition: transform 0.3s ease;
        
        &:hover {
          transform: scale(1.05);
        }
      }
      
      .floating-elements {
        .floating-element {
          position: absolute;
          animation: float 3s ease-in-out infinite;
          
          &.floating-element-1 {
            top: 20%;
            left: 10%;
            animation-delay: 0s;
          }
          
          &.floating-element-2 {
            bottom: 20%;
            right: 10%;
            animation-delay: 1.5s;
          }
          
          img {
            max-width: 80px;
            height: auto;
          }
        }
      }
    }
    
    .product-info-container {
      .product-badge {
        .badge {
          font-size: rem(12);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }
      
      .product-title {
        font-size: rem(45);
        line-height: 1.2;
        color: color(black);
        
        @include media-breakpoint-down(md) {
          font-size: rem(35);
        }
      }
      
      .product-description {
        font-size: rem(16);
        line-height: 1.6;
      }
      
      .current-price {
        color: color(gold);
      }
      
      .color-selection {
        .color-options {
          .color-option {
            width: rem(40);
            height: rem(40);
            border: 2px solid transparent;
            transition: all 0.3s ease;
            
            &:hover {
              border-color: color(gold);
              transform: scale(1.1);
            }
            
            &.active {
              border-color: color(gold);
              box-shadow: 0 0 0 3px rgba(191, 147, 108, 0.3);
            }
          }
        }
      }
      
      .action-buttons {
        .btn {
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-radius: rem(8);
          padding: rem(12) rem(24);
          
          &.btn-primary {
            background-color: color(gold);
            border-color: color(gold);
            
            &:hover {
              background-color: darken(color(gold), 10%);
              border-color: darken(color(gold), 10%);
            }
          }
        }
      }
    }
    
    .product-grid-section {
      background-color: #f8f9fa;
      
      .section-title {
        font-size: rem(35);
        color: color(black);
        margin-bottom: rem(16);
        
        @include media-breakpoint-down(md) {
          font-size: rem(24);
        }
      }
      
      .section-subtitle {
        font-size: rem(16);
        line-height: 1.6;
      }
    }
    
    // Animations
    @keyframes float {
      0%, 100% {
        transform: translateY(0px);
      }
      50% {
        transform: translateY(-20px);
      }
    }
    
    // Responsive adjustments
    @include media-breakpoint-down(lg) {
      .hero-section {
        .row {
          .col-lg-6:first-child {
            order: 2;
          }
          .col-lg-6:last-child {
            order: 1;
            margin-bottom: rem(48);
          }
        }
      }
    }
    
    @include media-breakpoint-down(md) {
      .product-info-container {
        text-align: center;
        
        .action-buttons {
          flex-direction: column;
        }
      }
    }
  }
}
</style>